import { useMutation, useQueries } from "@tanstack/react-query";
import { instance } from "api/apiBase";
import {
  ButtonSize,
  Modal,
  Button,
  ButtonVariant,
  IconButton,
  Radio,
  Show
} from "shared/components";
import { Card } from "shared/components/Card";
import InfoIcon from "shared/icons/status-icons/info-icon.svg?react";
import { Application, MergeApplicationsError } from "types/application";
import { XClose } from "untitledui-js-base";
import { useNavigate } from "react-router-dom";

import { getErrorMessage } from "utils/error";
import { Loader } from "shared/components/Loader";
import { toast } from "react-toastify";
import { getFullName } from "shared/utils/get-full-name";
import { cx } from "shared/utils";
import { useState } from "react";
import { getApplicantRole } from "utils/getApplicantRole";
import { useUnitsPropertiesDictionary } from "shared/hooks/use-units-properties";
import { AxiosError } from "axios";

const getApplication = async (id: string) => {
  return instance<Application>(`/portal/v1/applications/${id}`).then(
    ({ data }) => data
  );
};

export const MergeApplicantsModal = ({
  isOpen,
  onClose,
  selectedApplicants
}: {
  isOpen: boolean;
  onClose: () => void;
  selectedApplicants: string[];
}) => {
  const dictionary = useUnitsPropertiesDictionary();
  const navigate = useNavigate();
  const [isConfirmStep, setIsConfirmStep] = useState<boolean>(false);
  const [selectedApplicationId, setSelectedApplicationId] = useState<
    string | undefined
  >();
  const applicationsMeta = useQueries({
    queries: selectedApplicants.map((applicantId) => ({
      queryKey: ["application", applicantId],
      queryFn: () => getApplication(applicantId)
    })),
    combine: (results) => {
      return {
        data: results.reduce((acc: Application[], result) => {
          if (result.data) acc.push(result.data);

          return acc;
        }, []),
        pending: results.some((result) => result.isPending),
        loading: results.some((result) => result.isLoading)
      };
    }
  });

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      return instance.post("/portal/v1/applications/merge", {
        target_application_id: selectedApplicationId,
        application_ids: applicationsMeta.data.reduce((acc, application) => {
          if (application.id !== Number(selectedApplicationId))
            acc.push(application.id);

          return acc;
        }, [] as number[])
      });
    },
    mutationKey: ["application", selectedApplicationId],
    onSuccess: () => {
      toast.success("Application successfully merged");
      navigate(`/applications/${selectedApplicationId}`);
    },
    onError: (error: AxiosError<MergeApplicationsError>) => {
      const allErrors = error.response?.data.validation_errors
        .map((el) => el.errors)
        .flat();
      if (allErrors) {
        if (
          allErrors.includes(
            "merging_applications_should_not_have_association_to_at_least_one_customer"
          )
        )
          toast.error(
            "This application already has an active applicant with the same contact details. Please cancel the duplicate applicant before attempting to merge these application groups."
          );
        else if (allErrors.includes("not_mergeable_status"))
          toast.error(
            "The target application must be in 'Pending decision', 'In progress' or 'Submitted' status"
          );
      } else toast.error(getErrorMessage(error));
    }
  });

  const allUsers = applicationsMeta.data.flatMap((applicant) => [
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    ...(applicant.applicants || []),
    ...(applicant.pending_magic_links || [])
  ]);

  return (
    <Modal
      opened={isOpen}
      onClose={onClose}
      panelClassName="max-w-[600px] max-h-[640px] w-full min-h-[300px] flex flex-col h-full"
    >
      <div className="p-6 border-b border-alpha-100 flex flex-col">
        <div className="flex justify-between items-center">
          <h1 className="text-xl text-content-1000 font-bold">
            {isConfirmStep
              ? "Confirm merging applications"
              : "Select the application to merge others into"}
          </h1>
          <IconButton
            onClick={onClose}
            borderLess
            className="ml-auto translate-x-2"
            Icon={XClose}
          />
        </div>

        <Show if={!isConfirmStep}>
          <p className="text-sm text-content-500 font-normal">
            All applicants and guarantors will be merged into one application.
            The new application will use the screening settings from the
            selected application. Individual applications will keep the original
            set of requested information, but any missing reports will be added
          </p>
        </Show>
      </div>

      <Show if={isConfirmStep}>
        <div className="bg-warning-50 grow-0 px-6 py-4 flex gap-4 items-center border-b border-alpha-100">
          <InfoIcon className="text-warning-500 h-5 w-5" />
          <p className="text-sm text-warning-700 font-medium">
            Merging applications can’t be undone, so make sure everything looks
            right
          </p>
        </div>
      </Show>

      <div className="h-full flex flex-col grow-1 overflow-y-auto px-6 py-4 bg-background-900 gap-4">
        {applicationsMeta.loading ? (
          <div className="w-full flex items-center justify-center h-full">
            <Loader />
          </div>
        ) : !isConfirmStep ? (
          applicationsMeta.data.map((application) => {
            return (
              <Card
                key={application.id}
                className={cx(
                  "px-6 py-4",
                  selectedApplicationId === String(application.id)
                    ? "border-primary-100"
                    : null
                )}
              >
                <div className="flex justify-between items-center border-b border-alpha-40">
                  <div className="flex flex-col">
                    <p className="text-sm text-content-400 mb-2">
                      Application group
                    </p>
                    <h3 className="truncate text-base text-content-1000 font-bold pb-4">
                      {application.applicants
                        .map((el) =>
                          getFullName({
                            firstName: el.first_name,
                            lastName: el.last_name,
                            middleName: el.middle_name,
                            short: true
                          })
                        )
                        .join(", ")}
                    </h3>
                  </div>
                  <Radio
                    name="criteriaEnabled"
                    id={String(application.id)}
                    checked={selectedApplicationId === String(application.id)}
                    onChange={() =>
                      setSelectedApplicationId(String(application.id))
                    }
                  />
                </div>

                <div className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <p className="text-xs text-content-400 font-normal">
                      {dictionary.Property}
                    </p>
                    <p className="text-sm text-content-1000 font-medium">
                      {application.property.name}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-content-400 font-normal">
                      {dictionary.Unit}
                    </p>
                    <p className="text-sm text-content-1000 font-medium">
                      {application.unit.title}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <p className="text-xs text-content-400 font-normal">
                      Screening settings
                    </p>
                    <p className="text-sm text-content-1000 font-medium">
                      {application.verification_template?.name || ""}
                    </p>
                  </div>
                </div>
              </Card>
            );
          })
        ) : (
          <Card className="px-6 py-4">
            <p className="text-sm text-content-400 mb-2">Application group</p>
            <h3 className="truncate text-base text-content-1000 font-bold pb-4 border-b border-alpha-100">
              {allUsers
                .map((applicant) =>
                  "first_name" in applicant &&
                  applicant.first_name &&
                  applicant.last_name
                    ? getFullName({
                        firstName: applicant.first_name,
                        lastName: applicant.last_name,
                        middleName: applicant.middle_name,
                        short: true
                      })
                    : applicant.email
                )
                .join(", ")}
            </h3>

            <ul className="mt-4">
              {allUsers.map((applicant) => (
                <li key={applicant.email} className="mb-4">
                  <p className="text-sm text-content-1000 mb-2 font-medium">
                    {"first_name" in applicant &&
                    applicant.first_name &&
                    applicant.last_name
                      ? getFullName({
                          firstName: applicant.first_name,
                          lastName: applicant.last_name,
                          middleName: applicant.middle_name,
                          short: true
                        })
                      : applicant.email}
                  </p>
                  <p className="text-xs text-content-400">
                    {getApplicantRole(applicant.role)}
                  </p>
                </li>
              ))}
            </ul>
          </Card>
        )}
      </div>
      <div className="flex h-[72px] justify-end px-6 py-4 border-t border-alpha-100 gap-2">
        <Button
          size={ButtonSize.SM}
          onClick={() => {
            if (isConfirmStep) {
              setIsConfirmStep(false);
              setSelectedApplicationId(undefined);
            } else onClose();
          }}
        >
          Back
        </Button>
        <Button
          variant={ButtonVariant.PRIMARY}
          onClick={() => {
            if (isConfirmStep) {
              if (!isPending) mutate();
            } else setIsConfirmStep(true);
          }}
          loading={isPending}
          size={ButtonSize.SM}
          disabled={
            applicationsMeta.loading || isPending || !selectedApplicationId
          }
        >
          {isConfirmStep ? "Complete merge" : "Continue"}
        </Button>
      </div>
    </Modal>
  );
};
